import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { buildingService } from '@/services/tycoon/buildingService';
import { BUILDINGS_CONSTANTS, TIME_CONSTANTS } from '@/config/gameConstants';
import type { OwnedBuilding } from '@/types/buildings';
import type { Employee, HiringProcess } from '@/types/tycoon';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';
import { createSyncMiddleware, type WithSyncControls } from './middleware/syncMiddleware';
import { DataMigrationService } from '../services/DataMigrationService';
import type { DataVersion } from '../utils/dataMigration';

export interface BuildingCapacity {
  officeWorkers: { used: number; total: number };
  factoryWorkers: { used: number; total: number };
  technicalWorkers: { used: number; total: number };
  total: number;
  used: number;
  available: number;
  usage: number;
}

export interface MaintenanceCosts {
  perBuilding: Array<{ buildingId: string; name: string; cost: number }>;
  totalMaintenance: number;
}

export interface BuildingStore extends WithSyncControls {
  // State
  buildings: OwnedBuilding[];
  isInitialized: boolean;
  
  // Actions
  setBuildings: (buildings: OwnedBuilding[]) => void;
  initializeWithStartingBuilding: (gameDate: { day: number; month: number; year: number }) => void;
  updateConstructionStatus: (gameDate: { day: number; month: number; year: number }) => void;
  purchaseBuilding: (
    buildingTypeId: string,
    gameDate: { day: number; month: number; year: number },
    cash: number,
    onCashUpdate: (newCash: number) => void,
    onEvent?: (event: {
      type: string;
      title: string;
      description: string;
      impact: 'positive' | 'negative' | 'neutral';
    }) => void
  ) => boolean;
  sellBuilding: (
    buildingId: string,
    employees: Employee[],
    cash: number,
    onCashUpdate: (newCash: number) => void,
    onEvent?: (event: {
      type: string;
      title: string;
      description: string;
      impact: 'positive' | 'negative' | 'neutral';
    }) => void
  ) => boolean;
  upgradeBuilding: (
    buildingId: string,
    gameDate: { day: number; month: number; year: number },
    cash: number,
    onCashUpdate: (newCash: number) => void,
    onEvent?: (event: {
      type: string;
      title: string;
      description: string;
      impact: 'positive' | 'negative' | 'neutral';
    }) => void
  ) => boolean;
  
  // Computed getters
  getCapacity: (employees: Employee[]) => BuildingCapacity;
  getMaintenanceCosts: () => MaintenanceCosts;
  getAvailableBuildings: (gameDate: { year: number; month: number }) => ReturnType<typeof buildingService.getAvailableBuildingTypes>;
  checkCapacityForHiring: (role: string, count: number, employees: Employee[], hiringProcesses?: HiringProcess[]) => { hasCapacity: boolean; message?: string };
  getCapacityWarning: (employees: Employee[]) => 'none' | 'low' | 'critical';
  getDailyMaintenanceCost: () => number;
  getAllBuildingStats: () => {
    totalBuildings: number;
    operationalBuildings: number;
    buildingsUnderConstruction: number;
    buildingsBeingUpgraded: number;
    totalCapacity: number;
    totalMaintenanceCost: number;
  };
  getCapacityBreakdown: (employees: Employee[]) => {
    officeWorkers: { used: number; total: number; percentage: number };
    factoryWorkers: { used: number; total: number; percentage: number };
    technicalWorkers: { used: number; total: number; percentage: number };
    total: { used: number; total: number; percentage: number };
  };
  
  // Reset
  resetBuildings: () => void;

  // Hydration from save
  hydrateFromSave: (saveData: {
    buildings?: Array<{
      id: string;
      buildingTypeId: string;
      purchaseDate: { day: number; month: number; year: number };
      constructionCompleteDate?: { day: number; month: number; year: number };
      isUnderConstruction?: boolean;
      name?: string;
    }>;
  } | undefined) => void;
}

// Current version of the building store schema
const BUILDING_STORE_VERSION: DataVersion = { major: 1, minor: 0, patch: 0 };


// Custom sync configuration for building store
const buildingSyncConfig = {
  routing: {
    rules: [
      // Building data goes to IndexedDB
      {
        match: (path) => path === 'buildings' || path.startsWith('buildings.'),
        target: 'indexedDB' as const,
        priority: 85
      },
      // Default to IndexedDB
      {
        match: () => true,
        target: 'indexedDB' as const,
        priority: 0
      }
    ],
    defaultTarget: 'indexedDB' as const
  },
  behavior: {
    debounceDelay: 500, // Short delay for building updates
    batchSize: 10,
    enableAutoSync: true,
    syncOnReconnect: true,
    maxRetries: 3
  },
  hooks: {
    afterSync: (result) => {
      if (result.success) {
        // console.debug('[BuildingStore] Synced successfully to', result.target);
      }
    },
    onError: (error) => {
      console.error('[BuildingStore] Sync error:', error.message);
    }
  },
  monitoring: {
    trackMetrics: true,
    reportInterval: 60000, // Report metrics every minute
  }
};

// Initialize migration service for building store
const migrationService = DataMigrationService.getInstance();

// Register migration schemas
migrationService.registerMigrationSchemas('buildingStore', [
  {
    version: { major: 1, minor: 0, patch: 0 },
    description: 'Initial building store schema',
    up: async (data) => data,
    down: async (data) => data
  }
]);

export const useBuildingStore = create<BuildingStore>()(
  devtools(
    createSyncMiddleware(
      'buildingStore',
      buildingSyncConfig
    )(
      (set, get) => ({
        // Initial state
        buildings: [],
        isInitialized: false,

        // Actions
        setBuildings: (buildings) =>
          set({ buildings }, false, 'setBuildings'),

        initializeWithStartingBuilding: (gameDate) =>
          set((state) => {
            if (state.isInitialized || state.buildings.length > 0) {
              return state;
            }
            
            const startingBuilding = buildingService.purchaseBuilding(
              BUILDINGS_CONSTANTS.startingBuilding,
              gameDate
            );
            
            return {
              buildings: [startingBuilding],
              isInitialized: true
            };
          }, false, 'initializeWithStartingBuilding'),

        updateConstructionStatus: (gameDate) =>
          set((state) => {
            let updatedBuildings = buildingService.updateConstructionStatus(state.buildings, gameDate);
            updatedBuildings = buildingService.updateUpgradeStatus(updatedBuildings, gameDate);
            
            const hasChanges = updatedBuildings.some((building, index) => 
              building.isUnderConstruction !== state.buildings[index].isUnderConstruction ||
              building.isUpgrading !== state.buildings[index].isUpgrading ||
              building.buildingTypeId !== state.buildings[index].buildingTypeId
            );

            return hasChanges ? { buildings: updatedBuildings } : state;
          }, false, 'updateConstructionStatus'),

        purchaseBuilding: (buildingTypeId, gameDate, cash, onCashUpdate, onEvent) => {
          const state = get();
          const { canPurchase, reason } = buildingService.canPurchaseBuilding(
            buildingTypeId,
            cash,
            { year: gameDate.year, month: gameDate.month },
            state.buildings
          );

          if (!canPurchase) {
            onEvent?.({
              type: 'building',
              title: 'Cannot Purchase Building',
              description: reason || 'Unable to purchase this building',
              impact: 'negative'
            });
            return false;
          }

          const buildingType = buildingService.getBuildingType(buildingTypeId);
          if (!buildingType) return false;

          // Create the new building
          const newBuilding = buildingService.purchaseBuilding(buildingTypeId, gameDate);

          // Update state
          set((state) => ({
            buildings: [...state.buildings, newBuilding]
          }), false, 'purchaseBuilding');
          
          onCashUpdate(cash - buildingType.cost);

          // Fire event
          const totalCapacity = buildingType.capacity.officeWorkers + buildingType.capacity.factoryWorkers + buildingType.capacity.technicalWorkers;
          onEvent?.({
            type: 'building',
            title: 'Building Purchased',
            description: `Purchased ${buildingType.name} for ${formatCurrency(buildingType.cost)}. Capacity increased by ${totalCapacity} employees.`,
            impact: 'positive'
          });

          return true;
        },

        sellBuilding: (buildingId, employees, cash, onCashUpdate, onEvent) => {
          const state = get();
          const building = state.buildings.find(b => b.id === buildingId);
          if (!building) return false;

          const buildingType = buildingService.getBuildingType(building.buildingTypeId);
          if (!buildingType || !buildingType.canSell) {
            onEvent?.({
              type: 'building',
              title: 'Cannot Sell Building',
              description: 'This building cannot be sold',
              impact: 'negative'
            });
            return false;
          }

          // Check if we would have enough capacity after selling
          const newBuildings = state.buildings.filter(b => b.id !== buildingId);
          const newCapacity = buildingService.calculateCapacity(newBuildings, employees);
          
          if (newCapacity.total < employees.length) {
            onEvent?.({
              type: 'building',
              title: 'Cannot Sell Building',
              description: 'Cannot sell this building - it would leave employees without workspace',
              impact: 'negative'
            });
            return false;
          }

          // Sell the building
          set({ buildings: newBuildings }, false, 'sellBuilding');
          onCashUpdate(cash + (buildingType.sellValue || 0));

          onEvent?.({
            type: 'building',
            title: 'Building Sold',
            description: `Sold ${buildingType.name} for ${formatCurrency(buildingType.sellValue || 0)}`,
            impact: 'neutral'
          });

          return true;
        },

        upgradeBuilding: (buildingId, gameDate, cash, onCashUpdate, onEvent) => {
          const state = get();
          const building = state.buildings.find(b => b.id === buildingId);
          if (!building) return false;

          const { canUpgrade, upgradeToId, cost, reason } = buildingService.canUpgradeBuilding(
            building,
            cash,
            { year: gameDate.year, month: gameDate.month }
          );

          if (!canUpgrade) {
            onEvent?.({
              type: 'building',
              title: 'Cannot Upgrade Building',
              description: reason || 'Unable to upgrade this building',
              impact: 'negative'
            });
            return false;
          }

          const buildingType = buildingService.getBuildingType(building.buildingTypeId);
          const upgradeToBuildingType = buildingService.getBuildingType(upgradeToId!);
          
          if (!buildingType || !upgradeToBuildingType) return false;

          // Start the upgrade
          const upgradedBuilding = buildingService.startUpgrade(building, gameDate);
          
          // Update state
          set((state) => ({
            buildings: state.buildings.map(b => b.id === buildingId ? upgradedBuilding : b)
          }), false, 'upgradeBuilding');
          
          onCashUpdate(cash - (cost || 0));

          // Fire event
          onEvent?.({
            type: 'building',
            title: 'Building Upgrade Started',
            description: `Started upgrading ${buildingType.name} to ${upgradeToBuildingType.name} for ${formatCurrency(cost || 0)}. The upgrade will take ${buildingType.upgradeTime || 0} days.`,
            impact: 'positive'
          });

          return true;
        },

        // Computed getters
        getCapacity: (employees) => {
          const state = get();
          return buildingService.calculateCapacity(state.buildings, employees);
        },

        getMaintenanceCosts: () => {
          const state = get();
          return buildingService.calculateMaintenanceCosts(state.buildings);
        },

        getAvailableBuildings: (gameDate) => {
          const state = get();
          return buildingService.getAvailableBuildingTypes(gameDate).filter(
            buildingType => {
              // Check if already owned (unless it's a building type that can have multiples)
              const alreadyOwned = state.buildings.some(b => b.buildingTypeId === buildingType.id);

              // HQ can only have one, so if owned, don't show in available
              if (buildingType.id === 'corporateHQ' && alreadyOwned) {
                return false;
              }

              // For other buildings, allow purchasing multiple
              return !alreadyOwned || (buildingType.id !== 'smallOffice'); // Can't buy multiple starting offices
            }
          );
        },

        checkCapacityForHiring: (role, count, employees, hiringProcesses) => {
          const state = get();
          return buildingService.hasCapacityForRole(role, count, state.buildings, employees, hiringProcesses);
        },

        getCapacityWarning: (employees) => {
          const state = get();
          const capacity = buildingService.calculateCapacity(state.buildings, employees);
          return buildingService.getCapacityWarningLevel(capacity);
        },

        getDailyMaintenanceCost: () => {
          const state = get();
          const maintenanceCosts = buildingService.calculateMaintenanceCosts(state.buildings);
          return maintenanceCosts.totalMaintenance / TIME_CONSTANTS.daysPerMonth;
        },

        getAllBuildingStats: () => {
          const state = get();
          const maintenanceCosts = buildingService.calculateMaintenanceCosts(state.buildings);
          const totalCapacity = state.buildings.reduce((sum, building) => {
            const buildingType = buildingService.getBuildingType(building.buildingTypeId);
            if (!buildingType) return sum;
            const cap = buildingType.capacity;
            return sum + cap.officeWorkers + cap.factoryWorkers + cap.technicalWorkers;
          }, 0);

          return {
            totalBuildings: state.buildings.length,
            operationalBuildings: state.buildings.filter(b => !b.isUnderConstruction && !b.isUpgrading).length,
            buildingsUnderConstruction: state.buildings.filter(b => b.isUnderConstruction).length,
            buildingsBeingUpgraded: state.buildings.filter(b => b.isUpgrading).length,
            totalCapacity,
            totalMaintenanceCost: maintenanceCosts.totalMaintenance
          };
        },

        getCapacityBreakdown: (employees) => {
          const state = get();
          const capacity = buildingService.calculateCapacity(state.buildings, employees);
          
          // Count employees by role
          const officeWorkersUsed = employees.filter(e => 
            ['ceo', 'cfo', 'marketingManager', 'salesRepresentative', 'hrManager', 'officeManager'].includes(e.role)
          ).length;
          
          const factoryWorkersUsed = employees.filter(e => 
            ['factoryWorker', 'qualityAssurance', 'supplyChainManager'].includes(e.role)
          ).length;
          
          const technicalWorkersUsed = employees.filter(e => 
            ['leadEngineer', 'engineer', 'uxDesigner', 'productManager', 'researcher'].includes(e.role)
          ).length;

          return {
            officeWorkers: {
              used: officeWorkersUsed,
              total: capacity.officeWorkers.total,
              percentage: capacity.officeWorkers.total > 0 ? (officeWorkersUsed / capacity.officeWorkers.total) * 100 : 0
            },
            factoryWorkers: {
              used: factoryWorkersUsed,
              total: capacity.factoryWorkers.total,
              percentage: capacity.factoryWorkers.total > 0 ? (factoryWorkersUsed / capacity.factoryWorkers.total) * 100 : 0
            },
            technicalWorkers: {
              used: technicalWorkersUsed,
              total: capacity.technicalWorkers.total,
              percentage: capacity.technicalWorkers.total > 0 ? (technicalWorkersUsed / capacity.technicalWorkers.total) * 100 : 0
            },
            total: {
              used: capacity.used,
              total: capacity.total,
              percentage: capacity.usage
            }
          };
        },

        // Reset
        resetBuildings: () =>
          set({ buildings: [], isInitialized: false }, false, 'resetBuildings'),

        // Hydration from save
        hydrateFromSave: (saveData) =>
          set(
            (state) => {
              if (!saveData?.buildings) return state;

              // Convert save format to OwnedBuilding format
              const buildings: OwnedBuilding[] = saveData.buildings.map(b => ({
                id: b.id,
                buildingTypeId: b.buildingTypeId,
                purchaseDate: b.purchaseDate,
                constructionCompleteDate: b.constructionCompleteDate,
                isUnderConstruction: b.isUnderConstruction ?? false,
                isUpgrading: false,
                upgradeStartDate: undefined,
                upgradeCompleteDate: undefined,
                upgradeToId: undefined,
                name: b.name,
              }));

              return {
                buildings,
                isInitialized: buildings.length > 0,
              };
            },
            false,
            'hydrateFromSave'
          ),
      })
    ),
    {
      name: 'building-store',
      serialize: {
        options: {
          map: true,
          set: true,
          date: true,
          error: true,
          regex: true,
          undefined: true,
          nan: true,
          infinity: true,
          symbol: false
        }
      }
    }
  )
);

// Selectors
export const selectBuildings = (state: BuildingStore) => state.buildings;
export const selectIsInitialized = (state: BuildingStore) => state.isInitialized;

// Computed selectors
export const selectBuildingCount = (state: BuildingStore) => state.buildings.length;
export const selectOperationalBuildings = (state: BuildingStore) => 
  state.buildings.filter(b => !b.isUnderConstruction && !b.isUpgrading);
export const selectBuildingsUnderConstruction = (state: BuildingStore) => 
  state.buildings.filter(b => b.isUnderConstruction);
export const selectBuildingsBeingUpgraded = (state: BuildingStore) => 
  state.buildings.filter(b => b.isUpgrading);

// Helper to persist building data immediately
export const persistBuildingDataNow = () => {
  // Get state if needed for persistence in the future
  // const state = useBuildingStore.getState();
  return Promise.resolve();
};

// Helper to load persisted building state
export const loadPersistedBuildingState = async () => {
  try {
    // Trigger migration if needed
    await migrationService.migrate('buildingStore', BUILDING_STORE_VERSION, {
      createBackup: true,
      validateAfter: true
    });
    
    // Load persisted data
    const persistedData = await migrationService.getCurrentVersion('buildingStore');
    return persistedData;
  } catch (error) {
    console.error('[BuildingStore] Failed to load persisted state:', error);
    return null;
  }
};